import React, { useEffect, useMemo, useState } from "react";
import { sensorGraphs, getSensorVariables } from "../../services/sensors/sensorsService";
import { Image } from "react-bootstrap";

import SensorEditModal from "./SensorEditModal";

import { useNavigate } from "react-router-dom";
import SensorsUtils from "../utils/SensorsUtils";
import { CURRENT_READINGS_PRIORITY_VARS, VARIABLES_WITH_ADJUSTED_CE, VARIABLES_WITH_ADJUSTED_PH } from "../utils/Constants";
import { operationForChangeVbValue, operationForChangeCeValue, operationForChangePhValue } from "../utils/AdjustData";
import { useUser } from "../../context/UserContext";

const VARIABLE_LABEL_OVERRIDES = {
  VB: 'Batería',
};

const SensorCard = ({ values, onUpdate, currentReadings, onAccessLost }) => {
  const [sensor, setSensor] = useState(values);
  const [showConfig, setShowConfig] = useState(false);
  const [now, setNow] = useState(Date.now());
  const { user } = useUser();
  const canSeeCurrentReadings = !!(user && user.authorities && user.authorities.includes('GET_OWN_CROPS'));
  // El vendedor edita los equipos de sus préstamos; los de sus clientes (alquiler o compra) solo
  // los ve (editable = false). El backend verifica lo mismo
  const canEditSensor = values.editable !== false && !!(user && user.authorities
    && (user.authorities.includes('UPDATE_SENSOR_SMALL') || user.authorities.includes('MANAGE_TRIALS')));

  useEffect(() => {
    const interval = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(interval);
  }, []);

  const sortedReadings = useMemo(() => {
    if (!currentReadings) return [];
    return [...currentReadings].sort((a, b) => {
      const rankA = CURRENT_READINGS_PRIORITY_VARS.indexOf(a.variableId);
      const rankB = CURRENT_READINGS_PRIORITY_VARS.indexOf(b.variableId);
      return (rankA === -1 ? CURRENT_READINGS_PRIORITY_VARS.length : rankA)
        - (rankB === -1 ? CURRENT_READINGS_PRIORITY_VARS.length : rankB);
    });
  }, [currentReadings]);

  // Freshness is shown once per card (like the "Mis cultivos" header), based on the
  // battery reading's timestamp since it's reported on every device heartbeat
  // regardless of which environmental sensors are attached.
  const freshnessDate = useMemo(() => {
    if (!currentReadings || currentReadings.length === 0) return null;
    const vbReading = currentReadings.find(r => r.variableId === 'VB');
    if (vbReading) return vbReading.date;
    return currentReadings.reduce((latest, r) =>
      (!latest || new Date(r.date) > new Date(latest)) ? r.date : latest, null);
  }, [currentReadings]);

  const minutesSinceLastReading = freshnessDate
    ? Math.floor((now - new Date(freshnessDate).getTime()) / 60000)
    : null;
  const sensorName = values.name;
  const sensorDescription = values.description;
  const sensorNotifications = values.sendMail;
  const sensorCrop = values.crop;
  
  const navigate = useNavigate();

  // Abrir las gráficas sin modificar el sensor: no le quita al cliente la marca de sensor nuevo
  const openGraphsReadOnly = async () => {
    try {
      const sensorVariablesString = await getSensorVariables(sensor.id);
      localStorage.setItem("actual-sensor", JSON.stringify({ ...sensor, sensorVariablesString }));
      navigate("/graphs");
    } catch (error) {
      if (error.response?.status === 401) return; // Lo maneja el interceptor global
      if (error.response?.status === 403) {
        alert("Ya no tienes acceso a este sensor (puede que el préstamo o el contrato haya terminado).");
        if (onAccessLost) onAccessLost(sensor.id);
      } else {
        console.error('Error al abrir el sensor: ', error);
        alert("No se pudo abrir el sensor, intenta de nuevo.");
      }
    }
  };

  const updateSensor = async (toGraph) => {
    if (localStorage.getItem("actual-sensor"))
      localStorage.setItem("actual-sensor", "");

    const sensorData = {
      ...sensor,
      ...(toGraph ? { checked: true } : {
        name: sensorName,
        crop: sensorCrop,
        description: sensorDescription,
        sendMail: sensorNotifications,
      }),
    };

    try {
      const response = await sensorGraphs(sensorData)

      localStorage.setItem("actual-sensor", JSON.stringify(response.data));

      if (toGraph)
        navigate("/graphs");
      else
        setSensor(response.data);
    } catch (error) {
      // La sesión vencida (401) la maneja el interceptor global; otros errores no cierran la sesión
      if (error.response?.status === 401) return;
      if (error.response?.status === 403) {
        // Pasa si el préstamo o el contrato terminó con la página abierta
        alert("Ya no tienes acceso a este sensor (puede que el préstamo o el contrato haya terminado).");
        if (onAccessLost) onAccessLost(sensor.id);
      } else {
        console.error('Error al abrir el sensor: ', error);
        alert("No se pudo abrir el sensor, intenta de nuevo.");
      }
    }
  };

  const onClick = () => {
    if (values.editable === false) {
      openGraphsReadOnly();
      return;
    }
    if (!sensor.checked)
      updateSensor(true);
    else {
      if(sensor.sensorVariablesString == null)
        updateSensor(true);
      else{
        localStorage.setItem("actual-sensor", JSON.stringify(sensor));
        navigate("/graphs");
      }
    }
  };

  const handleShowConfig = () => setShowConfig(true);
  const handleHideConfig = () => {
    setShowConfig(false);
    if (onUpdate)
      onUpdate(sensor);
  }

  // const checkboxChange = () => {
  //   setSensorNotifications(!sensorNotifications);
  // };

  const sensorInfo = {
    active: sensor.active,
    checked: sensor.checked,
    id: sensor.id,
    name: sensorName,
    crop: sensorCrop,
    description: sensorDescription,
    sendMail: sensorNotifications,
    sim: sensor.sim,
    sensorVariablesString: sensor.sensorVariablesString,
    startOperation: sensor.startOperation
  };

  return (
    <div onClick={onClick} className={`
      flex flex-col w-80 rounded-xl text-black bg-gray-50 hover:bg-gray-200 shadow-md cursor-pointer
      ${sensor.checked ? 'border-none' : 'border-4 border-red-500'}
    `}>
      <div className="mx-4 mt-4 overflow-hidden rounded-xl bg-white text-gray-700">
        <div className="w-full aspect-[1/1]">
          <Image width={1104} height={736}
            src={SensorsUtils.getBackgroundSensor(sensor.crop?.cropType?.name)}
            className="h-full w-full object-cover"
          />
        </div>
      </div>

      <div className="px-4 pt-4">
        <div className="flex items-center justify-between">
          <p className="block font-sans text-base font-medium text-black">
            {sensor.name}
          </p>

          <p className="block font-sans text-base font-medium text-black">
            {sensor.id}
          </p>
        </div>

        <div className="flex items-center">
          <p className="flex font-sans text-base font-medium items-center">
            <span className={`material-icons mr-2 ${sensor.active ? 'text-green-500' : 'text-red-500'}`}>{sensor.active ? 'check' : 'close'}</span> {sensor.active ? 'Activo' : 'Inactivo'}
          </p>
        </div>

        {canSeeCurrentReadings ? (
          <div className="mt-2">
            {sortedReadings.length === 0 && (
              <p className="text-sm text-gray-500 italic">Sin lecturas recientes.</p>
            )}
            {sortedReadings.length > 0 && (
              <div className="flex justify-end mb-1">
                <span className={`text-xs px-2 py-0.5 rounded-full ${SensorsUtils.getUpdateBadgeClass(minutesSinceLastReading)}`}>
                  {SensorsUtils.getUpdateLabel(minutesSinceLastReading)}
                </span>
              </div>
            )}
            {sortedReadings.map(reading => {
              const { icon, color } = SensorsUtils.getVariableIcon(reading.variableId);
              const label = VARIABLE_LABEL_OVERRIDES[reading.variableId] || reading.variableName;

              // Ajustes por sensor (mismos que ya se aplican en las gráficas históricas
              // vía VariableGraphContainer/requestMeasures) — se hacen antes de comparar
              // contra el rango configurado, para que el semáforo use el valor correcto.
              let displayValue = reading.value;
              let displayUnit = reading.unit;
              if (reading.variableId === 'VB') {
                const wrapper = [{ y: reading.value }];
                operationForChangeVbValue(wrapper);
                displayValue = wrapper[0].y;
                displayUnit = '%';
              } else if (VARIABLES_WITH_ADJUSTED_CE.includes(reading.variableId)) {
                const wrapper = [{ y: reading.value }];
                operationForChangeCeValue(wrapper, sensor.id);
                displayValue = wrapper[0].y;
              } else if (VARIABLES_WITH_ADJUSTED_PH.includes(reading.variableId)) {
                const wrapper = [{ y: reading.value }];
                operationForChangePhValue(wrapper, sensor.id);
                displayValue = wrapper[0].y;
              }
              if (reading.variableId === 'RHS') {
                displayValue = Math.round(displayValue);
                displayUnit = displayValue === 1 ? 'Pulso' : 'Pulsos';
              }

              // VB: el umbral se guarda en voltios pero acá se muestra en porcentaje
              // (sin conversión inversa todavía en ModalUpdateThreshold), así que no
              // se puede comparar de forma correcta — se omite el semáforo para batería.
              const outOfRange = reading.variableId !== 'VB' &&
                reading.minValue != null && reading.maxValue != null &&
                (displayValue < reading.minValue || displayValue > reading.maxValue);

              return (
                <div key={reading.variableId} className="flex items-center justify-between text-sm py-0.5">
                  <span className="flex items-center font-medium text-gray-700">
                    <span className={`material-icons-outlined text-sm mr-1 ${color}`}>{icon}</span>
                    {label}
                  </span>
                  <span className={`font-semibold ${outOfRange ? 'text-red-600' : 'text-gray-800'}`}>
                    {reading.variableId === 'RHS' ? displayValue : displayValue.toFixed(1)} {displayUnit}
                  </span>
                </div>
              );
            })}
          </div>
        ) : (
          <p className="block font-sans text-sm text-justify font-normal leading-normal text-gray-700">
            <b>Sensor EnVIT:</b> dispositivo autónomo equipado con un panel solar y una batería.
            Funciona hasta 3 años sin recalibración, garantizando datos precisos y confiables
            para una gestión eficiente.
          </p>
        )}
      </div>

      {/* Editar el sensor solo con permiso (el vendedor ve los equipos prestados en solo lectura) */}
      {canEditSensor && (
        <div className="flex items-center justify-center pb-2">
          {/* <button onClick={onClick} className="text-gray-700 hover:text-green-600 mr-2 transition duration-200"><span className="material-icons">visibility</span></button> */}
          <button onClick={(event) => {
            event.stopPropagation(); // Evita que el clic se propague al div padre
            handleShowConfig();
          }} className="text-blue-600 hover:text-green-600 transition duration-200"><span className="material-icons-outlined">edit</span></button>
        </div>
      )}

      {showConfig && 
        <div onClick={(e) => e.stopPropagation()}> {/* Evita la propagación aquí */}
          <SensorEditModal sensor={sensorInfo} onClose={handleHideConfig} onUpdate={setSensor} />
        </div>
      }
    </div>
  );
};

export default SensorCard;